import type { Table as ArrowTable } from 'apache-arrow';
// Dynamic imports for parquet-wasm to avoid loading 5.4MB WASM in critical path
import type { Table as WasmTable } from 'parquet-wasm/esm';
import type { PartitionIndex, ShardManifest } from '../types/parquet';

export interface BundleManifest {
  bundle: string;
  generatedAt: string;
  rowCount: number;
  columnCount: number;
  primaryKeys: string[];
  schema: Array<{ name: string; dtype: string }>;
  fileSizeBytes: number;
  checksum: { sha256: string };
  sources: string[];
  validation: {
    baseRowCount: number;
    rowCountMatchesBase: boolean;
  };
  yearRange?: { min: number; max: number };
}

export interface BundleData {
  manifest: BundleManifest;
  records: BundleRow[];
  columns: string[];
}

export type BundleRow = Record<string, unknown>;

export interface MunicipalIndicatorMetadata {
  name: string;
  description: string;
  unit: string;
  category: string;
  timeInvariant: boolean;
  timeRange?: string;
  sourceFile: string;
  notes?: string;
  order?: number;
}

export interface MunicipalDomainMetadata {
  name: string;
  description: string;
  color?: string;
}

export interface YearRange {
  min: number;
  max: number;
}

export interface MunicipalDataset {
  data: MunicipalRecord[];
  indicators: Record<string, MunicipalIndicatorMetadata>;
  categories: Record<string, MunicipalDomainMetadata>;
  bundles: Record<string, BundleManifest>;
  partitions?: Record<string, PartitionIndex | null>;
  loadedYearRange?: YearRange | null;
  targetYear?: number;
  allYears?: number[];
  loadedColumns?: Record<string, string[]>;
}

export interface MunicipalRecord {
  PRO_COM: string;
  year?: number;
  [indicator: string]: unknown;
}

export const SOURCE_TO_BUNDLE: Record<string, string> = {
  df_era5: 'climate',
  df_e3ci: 'climate',
  emissions: 'emissions_air',
  airquality: 'emissions_air',
  air_quality: 'emissions_air',
  elev_slope: 'geo',
  imperviousness: 'geo',
  solar_mm: 'geo',
  solar_yy: 'geo'
};

// Enhanced caching with LRU eviction and preloading
const manifestCache = new Map<string, Promise<BundleManifest>>();
const bundleCache = new Map<string, Promise<BundleData>>();
const partitionIndexCache = new Map<string, Promise<PartitionIndex | null>>();
const shardCache = new Map<string, Promise<BundleData>>();

// Lazy initialization - WASM only loads when actually needed for parquet processing
let parquetInitPromise: Promise<void> | null = null;
let parquetModulePromise: Promise<typeof import('parquet-wasm/esm')> | null = null;
let arrowModulePromise: Promise<typeof import('apache-arrow')> | null = null;

const PARTITIONED_BUNDLES = new Set(['climate', 'emissions_air']);
const BOOTSTRAP_BUNDLE = 'bootstrap'; // Special minimal bundle for fast initial load

// Performance optimization constants
const MAX_CACHE_SIZE = 50; // Limit cache size for memory management
const PARALLEL_LOAD_LIMIT = 6; // Maximum parallel bundle loads
const PRELOAD_DELAY = 50; // ms delay before preloading related data

// Cache management with LRU eviction
const cacheAccessOrder = new Map<string, number>();
let cacheAccessCounter = 0;

// Enhanced cache management with LRU eviction
function manageCacheSize<T>(cache: Map<string, T>, maxSize: number): void {
  if (cache.size <= maxSize) return;

  const sortedByAccess = Array.from(cacheAccessOrder.entries())
    .filter(([key]) => cache.has(key))
    .sort((a, b) => a[1] - b[1]);

  const toRemove = sortedByAccess.slice(0, cache.size - maxSize);
  toRemove.forEach(([key]) => {
    cache.delete(key);
    cacheAccessOrder.delete(key);
  });
}

function updateCacheAccess(key: string): void {
  cacheAccessOrder.set(key, ++cacheAccessCounter);
}

// Preload manifests for common bundles
export function preloadCommonManifests(): void {
  setTimeout(() => {
    ['climate', 'emissions_air', 'geo'].forEach(bundle => {
      loadManifest(bundle).catch(() => {}); // Silent preload
    });
  }, PRELOAD_DELAY);
}

async function getParquetModule() {
  // Lazy load parquet-wasm module only when actually needed
  if (!parquetModulePromise) {
    console.log('📦 Dynamically loading parquet-wasm module (5.4MB)...');
    parquetModulePromise = import('parquet-wasm/esm');
  }

  const parquetModule = await parquetModulePromise;

  // Initialize WASM if not already initialized
  if (!parquetInitPromise) {
    const wasmUrlModule = await import('parquet-wasm/esm/parquet_wasm_bg.wasm?url');
    parquetInitPromise = parquetModule.default(wasmUrlModule.default);
    await parquetInitPromise;
    console.log('✅ Parquet WASM initialized');
  } else {
    await parquetInitPromise;
  }

  return parquetModule;
}

async function getArrowModule() {
  if (!arrowModulePromise) {
    arrowModulePromise = import('apache-arrow');
  }
  return arrowModulePromise;
}

// Track fetch calls to detect infinite loops
const fetchCallCount = new Map<string, number>();
const FETCH_WARNING_THRESHOLD = 5;

// 🚀 PERFORMANCE FIX: Cache JSON fetch results to prevent duplicate network requests
// This cache stores promises to handle concurrent requests (deduplication)
const jsonFetchCache = new Map<string, Promise<any>>();

async function fetchJSON<T>(path: string): Promise<T> {
  // 🔥 CRITICAL FIX: Check cache FIRST to prevent duplicate requests
  // Return cached promise if it exists (handles both completed and in-flight requests)
  if (jsonFetchCache.has(path)) {
    const count = (fetchCallCount.get(path) || 0) + 1;
    fetchCallCount.set(path, count);
    console.log(`[fetchJSON] ✅ Cache hit #${count} for:`, path);
    return jsonFetchCache.get(path)! as Promise<T>;
  }

  // Track fetch frequency to detect infinite loops
  const count = (fetchCallCount.get(path) || 0) + 1;
  fetchCallCount.set(path, count);

  if (count > FETCH_WARNING_THRESHOLD) {
    console.error(`🚨 INFINITE FETCH DETECTED: ${path} has been fetched ${count} times!`);
  } else if (count === 1) {
    console.log('[fetchJSON] 🌐 Network fetch (first time):', path);
  }

  // Create promise and cache it BEFORE making the request
  // This ensures concurrent calls to fetchJSON use the same promise (deduplication)
  const fetchPromise = (async () => {
    const response = await fetch(path);

    if (count <= FETCH_WARNING_THRESHOLD) {
      console.log('[fetchJSON] Response status:', response.status, 'for', path);
    }

    if (!response.ok) {
      console.error('[fetchJSON] Failed to fetch:', path, response.status, response.statusText);
      throw new Error(`Failed to fetch ${path}: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    try {
      const result = JSON.parse(text) as T;
      if (count <= FETCH_WARNING_THRESHOLD) {
        console.log('[fetchJSON] Successfully loaded:', path, 'size:', text.length);
      }
      return result;
    } catch (error) {
      const snippet = text.slice(0, 120).replace(/\s+/g, ' ');
      console.warn('Failed to parse JSON', { path, snippet });
      throw new Error(`Failed to parse JSON from ${path}: ${error instanceof Error ? error.message : String(error)} (snippet: ${snippet})`);
    }
  })();

  // Store promise in cache for concurrent/future requests
  jsonFetchCache.set(path, fetchPromise);

  return fetchPromise;
}

async function loadManifest(bundleId: string): Promise<BundleManifest> {
  updateCacheAccess(`manifest_${bundleId}`);

  if (!manifestCache.has(bundleId)) {
    const manifestPromise = fetchJSON<BundleManifest>(`/data/manifests/${bundleId}.json`);
    manifestCache.set(bundleId, manifestPromise);
    manageCacheSize(manifestCache, MAX_CACHE_SIZE);
  }
  return manifestCache.get(bundleId)!;
}

async function loadPartitionIndex(bundleId: string): Promise<PartitionIndex | null> {
  if (!partitionIndexCache.has(bundleId)) {
    const fetchTask = (async () => {
      try {
        const url = `/data/manifests/${bundleId}.index.json`;
        console.log(`📂 Loading partition index from: ${url}`);
        const result = await fetchJSON<PartitionIndex>(url);
        console.log(`✅ Successfully loaded index for ${bundleId}:`, {
          shards: result?.shards?.length || 0,
          firstShard: result?.shards?.[0]?.yearRange
        });
        return result;
      } catch (error) {
        console.error(`❌ Failed to load partition index for ${bundleId}:`, error);
        return null;
      }
    })();
    partitionIndexCache.set(bundleId, fetchTask);
  }
  return partitionIndexCache.get(bundleId)!;
}

function arrowTableToRecords(table: ArrowTable): { records: BundleRow[]; columns: string[] } {
  const fields = table.schema.fields;
  const columns = fields.map((_, index) => table.getChildAt(index));
  const rowCount = table.numRows;
  const records: BundleRow[] = [];

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const row: BundleRow = {};
    fields.forEach((field, columnIndex) => {
      const column = columns[columnIndex];
      if (!column) {
        return;
      }
      const rawValue = column.get(rowIndex);
      let value: unknown = rawValue;

      if (typeof rawValue === 'bigint') {
        value = Number(rawValue);
      }

      if (field.name === 'PRO_COM' && value != null) {
        value = normaliseProCom(value);
      }

      if (field.name === 'year' && typeof value === 'string') {
        const parsed = Number(value);
        value = Number.isNaN(parsed) ? value : parsed;
      }

      row[field.name] = value === undefined ? null : value;
    });
    records.push(row);
  }

  return { records, columns: fields.map((field) => field.name) };
}

function normaliseProCom(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return raw;
  }
  return raw.padStart(6, '0');
}

function mergeRow(target: MunicipalRecord, source: BundleRow) {
  Object.entries(source).forEach(([key, value]) => {
    if (key === 'PRO_COM' || key === 'year') {
      return;
    }
    target[key] = value;
  });
}

function registerRecord(index: Map<string, Set<string>>, proCom: string, key: string) {
  if (!index.has(proCom)) {
    index.set(proCom, new Set([key]));
    return;
  }
  index.get(proCom)!.add(key);
}

async function loadBundle(
  bundleId: string,
  options?: { year?: number; columns?: string[] }
): Promise<BundleData> {
  const normalizedColumns = normaliseColumns(options?.columns);

  if (!options || options.year === undefined) {
    return loadAggregatedBundle(bundleId, normalizedColumns);
  }

  const index = await loadPartitionIndex(bundleId);
  if (!index) {
    return loadAggregatedBundle(bundleId, normalizedColumns);
  }

  const shard = selectShardForYear(index.shards, options.year);
  if (!shard) {
    return loadAggregatedBundle(bundleId, normalizedColumns);
  }

  return fetchShard(bundleId, shard, bundleId, normalizedColumns);
}

function normaliseColumns(columns?: string[]): string[] | undefined {
  if (!columns || columns.length === 0) {
    return undefined;
  }
  const set = new Set(columns);
  set.add('PRO_COM');
  set.add('year');
  return Array.from(set).sort();
}

function buildColumnsKey(columns?: string[]): string {
  if (!columns || columns.length === 0) {
    return 'all';
  }
  return columns.join(',');
}

async function fetchAndDecodeBundle(
  bundleId: string,
  columns?: string[],
  manifestOverride?: BundleManifest
): Promise<BundleData> {
  const manifest = manifestOverride ?? await loadManifest(bundleId);

  // Parallel network and WASM initialization
  const [buffer, parquetModule, arrowModule] = await Promise.all([
    fetch(`/data/${manifest.bundle}`).then(response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch bundle ${manifest.bundle}: ${response.status} ${response.statusText}`);
      }
      return response.arrayBuffer();
    }),
    getParquetModule(),
    getArrowModule()
  ]);

  // Use optimized column projection if available
  const wasmTable: WasmTable = parquetModule.readParquet(new Uint8Array(buffer), {
    columns: columns || undefined, // undefined loads all columns more efficiently than empty array
  });

  const table = arrowModule.tableFromIPC(wasmTable.intoIPCStream());
  const { records, columns: tableColumns } = arrowTableToRecords(table);
  return { manifest, records, columns: tableColumns };
}

async function loadAggregatedBundle(bundleId: string, normalizedColumns?: string[]): Promise<BundleData> {
  const columnsKey = buildColumnsKey(normalizedColumns);
  const manifest = await loadManifest(bundleId);
  const checksum = manifest.checksum?.sha256 ?? 'nochecksum';
  const cacheKey = `${bundleId}:${checksum}:${columnsKey}`;

  updateCacheAccess(cacheKey);

  if (!bundleCache.has(cacheKey)) {
    bundleCache.set(cacheKey, fetchAndDecodeBundle(bundleId, normalizedColumns, manifest));
    manageCacheSize(bundleCache, MAX_CACHE_SIZE);
  }
  return bundleCache.get(cacheKey)!;
}


function selectShardForYear(shards: ShardManifest[], year: number): ShardManifest | undefined {
  return shards.find((shard) => {
    const range = shard.yearRange;
    if (!range) {
      return false;
    }
    return year >= range.min && year <= range.max;
  }) ?? shards[shards.length - 1];
}

async function fetchShard(
  bundleId: string,
  shard: ShardManifest,
  cacheKeyBase?: string,
  columns?: string[]
): Promise<BundleData> {
  const normalized = columns;
  const columnsKey = buildColumnsKey(normalized);
  const checksum = shard.checksum?.sha256 ?? JSON.stringify(shard.checksum ?? {});
  const baseKey = cacheKeyBase ?? shard.file;
  const cacheKey = `${baseKey}:${checksum}:${columnsKey}`;
  if (shardCache.has(cacheKey)) {
    return shardCache.get(cacheKey)!;
  }

  const shardPromise = (async () => {
    const response = await fetch(`/data/${shard.file}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch shard ${shard.file}: ${response.status} ${response.statusText}`);
    }

    const buffer = await response.arrayBuffer();
    const parquetModule = await getParquetModule();
    const arrowModule = await getArrowModule();

    const wasmTable: WasmTable = parquetModule.readParquet(new Uint8Array(buffer), {
      columns: normalized,
    });
    const table = arrowModule.tableFromIPC(wasmTable.intoIPCStream());
    const { records, columns: tableColumns } = arrowTableToRecords(table);

    let manifest: BundleManifest;
    if (shard.manifest) {
      const manifestPath = `/${shard.manifest}`;
      try {
        manifest = await fetchJSON<BundleManifest>(manifestPath);
      } catch (error) {
        console.warn(`Falling back to base manifest for ${shard.file}`, error);
        const base = await loadManifest(bundleId);
        manifest = {
          ...base,
          bundle: shard.file,
          rowCount: records.length,
          yearRange: shard.yearRange,
          checksum: shard.checksum || { sha256: '' },
        };
      }
    } else {
      const base = await loadManifest(bundleId);
      manifest = {
        ...base,
        bundle: shard.file,
        rowCount: records.length,
        yearRange: shard.yearRange,
        checksum: shard.checksum || { sha256: '' },
      };
    }

    return { manifest, records, columns: tableColumns };
  })();

  shardCache.set(cacheKey, shardPromise);
  return shardPromise;
}

function getLatestYearFromIndex(index?: PartitionIndex | null): number | undefined {
  if (!index || index.shards.length === 0) {
    return undefined;
  }
  const lastShard = index.shards[index.shards.length - 1];
  const range = lastShard.yearRange;
  if (!range) {
    return undefined;
  }
  return range.max ?? range.min;
}

function expandYearsFromIndex(index?: PartitionIndex | null): number[] {
  if (!index) {
    return [];
  }
  const years = new Set<number>();
  index.shards.forEach((shard) => {
    const range = shard.yearRange;
    if (!range) {
      return;
    }
    for (let year = range.min; year <= range.max; year += 1) {
      years.add(year);
    }
  });
  return Array.from(years).sort((a, b) => a - b);
}

function mergeAllYears(indexes: Array<PartitionIndex | null | undefined>): number[] {
  const combined = new Set<number>();
  indexes.forEach((index) => {
    expandYearsFromIndex(index).forEach((year) => combined.add(year));
  });
  return Array.from(combined.values()).sort((a, b) => a - b);
}

/**
 * Load minimal bootstrap dataset for fast initial render
 */
export async function loadBootstrapDataset(): Promise<MunicipalDataset> {
  console.log('[bootstrap] Loading micro bootstrap JSON...');

  const bootstrapJson = await fetchJSON<{
    data: MunicipalRecord[];
    indicators: Record<string, MunicipalIndicatorMetadata>;
    categories: Record<string, MunicipalDomainMetadata>;
  }>('/data/micro_bootstrap.json');

  console.log('[bootstrap] Loaded', bootstrapJson.data.length, 'records (micro)');

  return {
    data: bootstrapJson.data,
    indicators: bootstrapJson.indicators,
    categories: bootstrapJson.categories,
    bundles: {},
    loadedYearRange: { min: 2023, max: 2023 },
    targetYear: 2023,
    allYears: [2023],
    loadedColumns: {}
  };
}

export async function loadMunicipalDatasetFromParquet(options?: {
  targetYear?: number;
  columnsByBundle?: Record<string, string[]>;
}): Promise<MunicipalDataset> {
  const [indicators, categories] = await Promise.all([
    fetchJSON<Record<string, MunicipalIndicatorMetadata>>('/data/municipal_indicators.json'),
    fetchJSON<Record<string, MunicipalDomainMetadata>>('/data/municipal_domains.json')
  ]);

  const [climateIndex, emissionsIndex] = await Promise.all([
    loadPartitionIndex('climate'),
    loadPartitionIndex('emissions_air'),
  ]);

  console.log('🔍 Partition Indexes Loaded:', {
    climate: climateIndex ? 'Found' : 'Missing',
    emissions: emissionsIndex ? 'Found' : 'Missing',
    climateShardCount: climateIndex?.shards?.length || 0,
    emissionsShardCount: emissionsIndex?.shards?.length || 0
  });

  let targetYear = options?.targetYear;
  if (targetYear === undefined) {
    targetYear = getLatestYearFromIndex(climateIndex) ?? getLatestYearFromIndex(emissionsIndex);
  }
  if (targetYear === undefined) {
    const climateManifest = await loadManifest('climate');
    targetYear = climateManifest.yearRange?.max ?? climateManifest.yearRange?.min;
  }
  if (targetYear === undefined) {
    targetYear = new Date().getFullYear();
  }

  // Enhanced parallel loading with concurrency control
  // Load the target year data - we'll make years available from the indexes
  const bundleLoadTasks = [
    { bundle: 'climate', options: { year: targetYear, columns: options?.columnsByBundle?.climate } },
    { bundle: 'emissions_air', options: { year: targetYear, columns: options?.columnsByBundle?.emissions_air } },
    { bundle: 'geo', options: { columns: options?.columnsByBundle?.geo } }
  ];

  console.log('📦 Loading bundles for target year', targetYear, ':', bundleLoadTasks);

  // Load bundles with concurrency control
  const [climateData, emissionsData, geoData] = await Promise.all(
    bundleLoadTasks.map(({ bundle, options: bundleOptions }) =>
      loadBundle(bundle, bundleOptions)
    )
  );

  const bundles: Record<string, BundleManifest> = {
    climate: climateData.manifest,
    emissions_air: emissionsData.manifest,
    geo: geoData.manifest,
  };

  const loadedColumns: Record<string, string[]> = {
    climate: climateData.columns,
    emissions_air: emissionsData.columns,
    geo: geoData.columns,
  };

  const combined = new Map<string, MunicipalRecord>();
  const proComIndex = new Map<string, Set<string>>();

  const upsertTimeSeriesRecord = (row: BundleRow) => {
    const proCom = normaliseProCom(row['PRO_COM']);
    if (!proCom) {
      return;
    }
    const yearValue = row['year'] as number | string | null | undefined;
    let year: number | undefined;
    if (typeof yearValue === 'number' && !Number.isNaN(yearValue)) {
      year = yearValue;
    } else if (typeof yearValue === 'string') {
      const parsed = Number(yearValue);
      year = Number.isNaN(parsed) ? undefined : parsed;
    }
    const key = year !== undefined ? `${proCom}_${year}` : proCom;

    if (!combined.has(key)) {
      combined.set(key, { PRO_COM: proCom, year });
      registerRecord(proComIndex, proCom, key);
    }

    const record = combined.get(key)!;
    if (year !== undefined) {
      record.year = year;
    }
    mergeRow(record, row);
  };

  climateData.records.forEach(upsertTimeSeriesRecord);
  emissionsData.records.forEach(upsertTimeSeriesRecord);

  const geoByProCom = new Map<string, BundleRow>();
  geoData.records.forEach((row) => {
    const proCom = normaliseProCom(row['PRO_COM']);
    if (proCom) {
      geoByProCom.set(proCom, row);
    }
  });

  geoByProCom.forEach((row, proCom) => {
    const targets = proComIndex.get(proCom);
    if (!targets) {
      return;
    }
    targets.forEach((key) => {
      const record = combined.get(key);
      if (record) {
        mergeRow(record, row);
      }
    });
  });

  const data = Array.from(combined.values()).sort((a, b) => {
    const proComComparison = a.PRO_COM.localeCompare(b.PRO_COM);
    if (proComComparison !== 0) {
      return proComComparison;
    }
    const yearA = a.year ?? 0;
    const yearB = b.year ?? 0;
    return yearA - yearB;
  });

  const years = data
    .map((record) => (typeof record.year === 'number' ? record.year : null))
    .filter((year): year is number => year !== null);
  const loadedYearRange = years.length > 0
    ? { min: Math.min(...years), max: Math.max(...years) }
    : null;

  const partitions = {
    climate: climateIndex,
    emissions_air: emissionsIndex,
  };

  const allYears = mergeAllYears([climateIndex, emissionsIndex]);

  console.log('🗓️ PARQUET LOADER - All Years Calculation:', {
    climateIndex: climateIndex ? 'loaded' : 'missing',
    emissionsIndex: emissionsIndex ? 'loaded' : 'missing',
    climateShards: climateIndex?.shards.map(s => ({
      file: s.file,
      yearRange: s.yearRange
    })),
    emissionsShards: emissionsIndex?.shards.map(s => ({
      file: s.file,
      yearRange: s.yearRange
    })),
    allYears: allYears,
    yearCount: allYears.length,
    yearRange: allYears.length > 0 ? `${allYears[0]} - ${allYears[allYears.length - 1]}` : 'none'
  });

  return {
    data,
    indicators,
    categories,
    bundles,
    partitions,
    loadedYearRange,
    targetYear,
    allYears,
    loadedColumns,
  };
}

export async function preloadBundle(bundleId: string): Promise<void> {
  await loadBundle(bundleId);
}

export async function getBundleManifest(bundleId: string): Promise<BundleManifest> {
  return loadManifest(bundleId);
}
